import {css, StyleSheet} from 'aphrodite';
import React, {useRef, useState} from 'react';

import {StudyDesignType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {Pagination} from 'lib/Listings';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import PlateMapDisplayLayerDropdown from 'samples/plate-maps/display-layer-controls/PlateMapDisplayLayerDropdown';
import usePlateMapDisplayAttributeState from 'samples/plate-maps/hooks/usePlateMapDisplayAttributeState';
import {FrozenEntityChipData} from 'samples/plate-maps/plate-record/PlateRecordConstants';
import {
  PlateMaterialInput,
  PlateMap,
  ReagentAssignmentGroup,
  REAGENTS_ATTRIBUTE_SENTINEL,
  SchematizedDisplayLayerMetadata,
  SupportedAdditionalAttribute,
  TEST_DILUTION_FACTOR_ATTRIBUTE_SENTINEL,
  WELL_ROLE_ATTRIBUTE_SENTINEL,
} from 'samples/plate-maps/PlateMapConstants';
import PlateMapPane from 'samples/plate-maps/PlateMapPane';
import {
  getDisplayLayerAttributeKey,
  getPaginationOffset,
  shouldShowTestOrderDilutionInfo,
} from 'samples/plate-maps/PlateMapUtil';
import PlateRecordDetailTable from 'samples/plate-maps/transfer-table/PlateRecordDetailTable';
import {GRAY_DARKER, GRAY_LIGHT} from 'ui-library/Colors';
import Modal from 'ui-library/modals/Modal';

const MAX_PLATE_HEIGHT = 'calc(100vh - 400px)';

export interface PlateRecordDetailModalProps {
  plateMapType: PlateMapType;
  /** The plate map representing this transfer. */
  plateMap: PlateMap;
  plateDataParametersOptions: Array<SchematizedDisplayLayerMetadata>;
  plateMaterialInputs?: Array<PlateMaterialInput>;
  wellSchemaOptions: Array<SchematizedDisplayLayerMetadata>;
  frozenEntityChipData: {[id: string]: FrozenEntityChipData};
  frozenReagentAssignmentGroups: {[id: string]: ReagentAssignmentGroup} | null;
  /* Design type of the associated procedure's study. This controls showing test order/dilution related
  information only for sample testing studies. */
  procedureStudyDesignType: StudyDesignType | null;
  onRequestClose: () => void;
}

/**
 * A modal to display the results of a Plate Map based bulk container transfer.
 *
 * Like Well View in `FillPlateModal`, this modal displays table rows based on which wells in the plate map
 * are selected. It also supports viewing plate display layers on the plate map viz.
 *
 * @param plateMapType The type of plate map (record vs. design).
 * @param plateMap The plate map representing the transfer.
 * @param plateDataParametersOptions The list of plate data parameter fields available on each well
 * @param plateMaterialInputs In a procedure context, a list of plate-used material inputs
 * @param wellSchemaOptions The list of schema fields available on each well
 * @param frozenEntityChipData A map from (bioentity, container) API ID to the frozen name & schema icon of that model, if provided by the record
 * @param frozenReagentAssignmentGroups Reagent assignment groups created from frozen data if provided by the record
 * @param onRequestClose A function to be called when the modal is closed
 * @param procedureStudyDesignType Design type of the procedure's study, if present.
 * @constructor
 */
export default function PlateRecordDetailModal({
  plateMapType,
  plateMap,
  plateDataParametersOptions,
  plateMaterialInputs,
  wellSchemaOptions,
  frozenEntityChipData,
  frozenReagentAssignmentGroups,
  onRequestClose,
  procedureStudyDesignType,
}: PlateRecordDetailModalProps): JSX.Element {
  const modalRef = useRef<HTMLDivElement>(null);
  const plateMapPaneWrapperRef = useRef<HTMLDivElement>(null);
  const [selectedWellIndices, setSelectedWellIndices] = useState<Set<number>>(new Set<number>());
  const [pagination, setPagination] = useState<Pagination>({
    offset: 0,
    limit: plateMap.size.numColumns,
    total: 0,
    isExact: true,
  });

  const {assignmentGroupsPerAttribute, selectedAttribute, setSelectedAttribute, displayLayer} =
    usePlateMapDisplayAttributeState(
      plateMap,
      REAGENTS_ATTRIBUTE_SENTINEL,
      false,
      frozenReagentAssignmentGroups == null
        ? undefined
        : {[REAGENTS_ATTRIBUTE_SENTINEL]: frozenReagentAssignmentGroups}
    );
  const baseAttributeOptions: Array<SupportedAdditionalAttribute> = [
    REAGENTS_ATTRIBUTE_SENTINEL,
    WELL_ROLE_ATTRIBUTE_SENTINEL,
  ];

  function onUpdateWellSelection(wellIndices: Set<number>): void {
    setSelectedWellIndices(wellIndices);
    setPagination({
      ...pagination,
      offset: getPaginationOffset(pagination.offset, pagination.limit, wellIndices.size),
      total: wellIndices.size,
    });
  }

  return (
    <Modal
      title={plateMapType === PlateMapType.RECORD ? 'Plate record' : 'Plate design'}
      fullWidth={true}
      buttons={[{text: 'Close', submit: true}]}
      onRequestClose={onRequestClose}
      // Since the submit button is a close button, close on submit.
      onRequestSubmit={onRequestClose}
      wrapInForm={false}
      closeableOnEscapeKey={true}
      expandHeight={true}
      ref={modalRef}
    >
      <div className={css(styles.wrapper)}>
        <div className={css(styles.mapPaneWrapper)} ref={plateMapPaneWrapperRef}>
          <PlateMapPane
            header={
              <div className={css(styles.layerDropdown)}>
                <PlateMapDisplayLayerDropdown
                  additionalAttributeOptions={
                    shouldShowTestOrderDilutionInfo(procedureStudyDesignType, PlateMapType.RECORD)
                      ? [...baseAttributeOptions, TEST_DILUTION_FACTOR_ATTRIBUTE_SENTINEL]
                      : baseAttributeOptions
                  }
                  selectedAttribute={selectedAttribute}
                  setSelectedAttribute={setSelectedAttribute}
                  plateDataParametersOptions={plateDataParametersOptions}
                  wellSchemaOptions={wellSchemaOptions}
                />
              </div>
            }
            plateMapDisplayParams={{
              plateMap,
              displayLayer,
              attribute: selectedAttribute,
              assignmentGroupsById:
                assignmentGroupsPerAttribute[getDisplayLayerAttributeKey(selectedAttribute)] ?? {},
              groupedValidationErrors: {},
            }}
            isPlateMapViewOnly={false}
            selectedWellIndices={selectedWellIndices}
            setSelectedWellIndices={onUpdateWellSelection}
            previewedWellIndices={new Set<number>()}
            modalRef={modalRef}
            deselectWellsOnClickRefs={[]}
            maxPlateViewHeight={MAX_PLATE_HEIGHT}
            showFinalMeasurements={true}
            plateMapType={PlateMapType.RECORD}
          />
        </div>
        <div className={css(styles.border)} />
        <div className={css(styles.tablePane)}>
          <div className={css(styles.wellSelectionHelpText)}>
            Select wells in the plate map to filter what rows are visible in the table below
          </div>
          <PlateRecordDetailTable
            pagination={pagination}
            plateMap={plateMap}
            plateMaterialInputs={plateMaterialInputs}
            reagentGroupsById={
              assignmentGroupsPerAttribute[getDisplayLayerAttributeKey(REAGENTS_ATTRIBUTE_SENTINEL)] as {
                [id: string]: ReagentAssignmentGroup;
              }
            }
            selectedWellIndices={selectedWellIndices}
            setPaginationOffset={(offset) => {
              setPagination({...pagination, offset});
            }}
            frozenEntityChipData={frozenEntityChipData}
            plateDataParameterMetadata={plateDataParametersOptions}
            wellSchemaFieldMetadata={wellSchemaOptions}
            testOrderInfoById={plateMap.testOrderDilutionInfoById ?? {}}
          />
        </div>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    gap: 16,
  },
  mapPaneWrapper: {
    paddingRight: 16,
    width: '33%',
    overflow: 'auto',
    display: 'flex',
  },
  layerDropdown: {
    maxWidth: '264px',
  },
  border: {
    flex: '0 0 0px',
    borderRight: `1px solid ${GRAY_LIGHT}`,
    marginTop: '-16px',
    marginBottom: '-16px',
  },
  tablePane: {
    flex: 1,
    overflow: 'auto',
  },
  wellSelectionHelpText: {
    color: GRAY_DARKER,
    marginBottom: '12px',
  },
});
